/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue';
import axios from 'axios';

export default {
  API: {
    OPTIMIZATION_FETCH_FILTERS: '/vaOptimization/fetchFilters',
    OPTIMIZATION_FETCH_CONVERSATIONS: '/vaOptimization/conversationSummary',
    OPTIMIZATION_FETCH_MESSAGES: '/vaOptimization/messagesSummary',
    OPTIMIZATION_FETCH_CORPUS_SUMMARY: '/vaOptimization/corpusSummary',
    OPTIMIZATION_FETCH_OVERVIEW: '/vaOptimization/panel/overview/optimizationOverviewCharts',
    OPTIMIZATION_OVERVIEW_CONVERSATIONS: '/vaOptimization/panel/overview/conversations',
    OPTIMIZATION_OVERVIEW_CONTAINED: '/vaOptimization/panel/overview/contained',
    OPTIMIZATION_OVERVIEW_FEEDBACK: '/vaOptimization/panel/overview/feedback',
    OPTIMIZATION_OVERVIEW_UNDERSTANDING: '/vaOptimization/panel/overview/understanding',
    OPTIMIZATION_OVERVIEW_NLUTRANING: '/vaOptimization/panel/overview/nluTraining',
    OPTIMIZATION_OVERVIEW_CORPUS: '/vaOptimization/panel/overview/corpus',
    OPTIMIZATION_OVERVIEW_HANDEDOFF: '/vaOptimization/panel/overview/handedOff',
    OPTIMIZATION_OVERVIEW_ISSUES: '/vaOptimization/panel/overview/issues',
    OPTIMIZATION_OVERVIEW_RESPONSE: '/vaOptimization/panel/overview/endResponse',
    OPTIMIZATION_FETCH_RESPONSE: '/vaOptimization/panel/responsePerformance/responsePerformanceCharts',

    OPTIMIZATION_COMPARISON_CONVERSATIONS: '/vaOptimization/panel/performanceComparison/conversations',
    OPTIMIZATION_COMPARISON_CONTAINED: '/vaOptimization/panel/performanceComparison/contained',
    OPTIMIZATION_COMPARISON_FEEDBACK: '/vaOptimization/panel/performanceComparison/feedback',
    OPTIMIZATION_COMPARISON_UNDERSTANDING: '/vaOptimization/panel/performanceComparison/understanding',
    OPTIMIZATION_COMPARISON_NLUTRANING: '/vaOptimization/panel/performanceComparison/nluTraining',
    OPTIMIZATION_COMPARISON_CORPUS: '/vaOptimization/panel/performanceComparison/corpus',
    OPTIMIZATION_COMPARISON_HANDEDOFF: '/vaOptimization/panel/performanceComparison/handedOff',
    OPTIMIZATION_COMPARISON_ISSUES: '/vaOptimization/panel/performanceComparison/issues',
    OPTIMIZATION_COMPARISON_RESPONSE: '/vaOptimization/panel/performanceComparison/hasEndResponse',

    // OPTIMIZATION_FETCH_INTENT_PERFORMANCE: '/vaOptimization/intentPerformance',
    OPTIMIZATION_FETCH_INTENT_PERFORMANCE: '/vaOptimization/panel/nluIntentPerformance/intentPerformanceCharts',
    OPTIMIZATION_FETCH_INTENT_PERFORMANCE_SUMMARY: '/vaOptimization/panel/nluIntentPerformance/intentPerformanceSummary',

    OPTIMIZATION_FETCH_INTENT_ACTION: '/vaOptimization/panel/nluIntentPerformance/intentActions',
    OPTIMIZATION_ADD_INTENT: '/vaOptimization/panel/nluIntentPerformance/saveIntent',
    OPTIMIZATION_REMOVE_INTENT: '/vaOptimization/panel/nluIntentPerformance/removeIntent',

    OPTIMIZATION_FETCH_UNCLASSIFIED_CATEGORIES: '/vaOptimization/panel/unclassifiedMessages/category',
    OPTIMIZATION_FETCH_UNCLASSIFIED_TOPICS: '/vaOptimization/panel/unclassifiedMessages/topic',
    // OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES: '/vaOptimization/panel/unclassifiedMessages/messages',
    OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES_WITH_TO_BE_ACTIONED: '/vaOptimization/panel/unclassifiedmessages/toBeActionedMessages',
    OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES_WITH_ACTIONED: '/vaOptimization/panel/unclassifiedmessages/actionedMessages',
    OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES_WITH_DISCARD: '/vaOptimization/panel/unclassifiedmessages/discardMessages',
    OPTIMIZATION_FETCH_UNCLASSIFIED_SAVE_MESSAGES: '/vaOptimization/panel/unclassifiedMessages/saveMessages',
    OPTIMIZATION_FETCH_UNCLASSIFIED_INTENTS: '/vaOptimization/panel/unclassifiedMessages/intents',

    OPTIMIZATION_FETCH_RESPONSE_PERFORMANCE_SUMMARY: '/vaOptimization/panel/responsePerformance/responsePerformanceSummary',
    OPTIMIZATION_ADD_RESPONSE: '/vaOptimization/panel/responsePerformance/saveResponse',
    OPTIMIZATION_REMOVE_RESPONSE: '/vaOptimization/panel/responsePerformance/removeResponse',
  },

  cachedHash: {},
  cancelTokens: [],

  getRequest(authUser, url, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;
      headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      return axios.get(url, { headers: headers, timeout: 300000 });
    } else {
      return null;
    }
  },

  postRequest(authUser, url, payload, headers) {
    if (authUser.accessToken) {
      if (headers == null) headers = {};
      headers['Authorization'] = 'Bearer ' + authUser.accessToken;
      headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
      if(authUser.sessionId) {
        headers['Auth-Host'] = authUser.authHost || "";
        headers['Auth-Session-Id'] = authUser.sessionId;
        headers['Auth-Product-Name'] = authUser.productName || "";
        headers['Auth-Options'] = authUser.authOptions || "";
      }

      let cancelTokenKey = url;
      if (this.cancelTokens[cancelTokenKey]) {
        // cancel request
        this.cancelTokens[cancelTokenKey].cancel();
      }
      this.cancelTokens[cancelTokenKey] = axios.CancelToken.source();

      let options = {
        timeout: 300000, // timeout in 5 mins
        headers: headers,
        cancelToken: this.cancelTokens[cancelTokenKey].token,
      };

      return axios.post(url, payload, options);
    } else {
      return null;
    }
  },

  fetchOptimizationFilters(authUser) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_FILTERS;
    return this.getRequest(authUser, url);
  },
  fetchOptimizationOverView(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_OVERVIEW;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationOverViewConversations(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_CONVERSATIONS;
    return this.postRequest(authUser, url, payload);
  },
  fetchOptimizationOverViewContained(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_CONTAINED;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationOverViewFeedback(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_FEEDBACK;
    return this.postRequest(authUser, url, payload);
  },

  fetchIntentAction(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_INTENT_ACTION;
    return this.postRequest(authUser, url, payload);
  },

  exportIntentAction(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_INTENT_ACTION;
    let headers = {};
    headers['Authorization'] = 'Bearer ' + authUser.accessToken;
    headers['Api-Configs'] = JSON.stringify(authUser.config.api.configs);
    if(authUser.sessionId) {
      headers['Auth-Host'] = authUser.authHost || "";
      headers['Auth-Session-Id'] = authUser.sessionId;
      headers['Auth-Product-Name'] = authUser.productName || "";
      headers['Auth-Options'] = authUser.authOptions || "";
    }

    let cancelTokenKey = url;
    if (this.cancelTokens[cancelTokenKey]) {
      // cancel request
      this.cancelTokens[cancelTokenKey].cancel();
    }
    this.cancelTokens[cancelTokenKey] = axios.CancelToken.source();

    let options = {
      timeout: 300000, // timeout in 5 mins
      headers: headers,
      cancelToken: this.cancelTokens[cancelTokenKey].token,
    };
    axios.post(url, payload, options).then((response) => {
      var fileURL = window.URL.createObjectURL(new Blob([response.data]));
      var fileLink = document.createElement('a');
      fileLink.href = fileURL;
      fileLink.setAttribute('download', 'actionItemDetails.csv');
      document.body.appendChild(fileLink);
      fileLink.click();
    });
  },

  fetchOptimizationOverViewUnderstanging(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_UNDERSTANDING;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationOverViewNluTraning(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_NLUTRANING;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationOverViewCorpus(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_CORPUS;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationOverViewHandedOff(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_HANDEDOFF;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationOverViewIssues(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_ISSUES;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationOverViewResponses(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_OVERVIEW_RESPONSE;
    return this.postRequest(authUser, url, payload);
  },
  fetchOptimizationConversationsSummary(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_CONVERSATIONS;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationMessagesSummary(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_MESSAGES;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationCorpusSummary(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_CORPUS_SUMMARY;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationComparisonConversations(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_CONVERSATIONS;
    return this.postRequest(authUser, url, payload);
  },
  fetchOptimizationComparisonContained(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_CONTAINED;
    return this.postRequest(authUser, url, payload);
  },
  fetchOptimizationComparisonFeedback(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_FEEDBACK;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationComparisonUnderstanding(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_UNDERSTANDING;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationComparisonNluTraning(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_NLUTRANING;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationComparisonCorpus(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_CORPUS;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationComparisonHandedOff(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_HANDEDOFF;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationComparisonIssues(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_ISSUES;
    return this.postRequest(authUser, url, payload);
  },
  fetchOptimizationComparisonResponse(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_COMPARISON_RESPONSE;
    return this.postRequest(authUser, url, payload);
  },
  fetchOptimizationIntentPerformance(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_INTENT_PERFORMANCE;
    return this.postRequest(authUser, url, payload);
  },
  fetchOptimizationResponse(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_RESPONSE;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationIntentPerformanceSummary(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_INTENT_PERFORMANCE_SUMMARY;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationUnclassifiedCategories(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_CATEGORIES;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationUnclassifiedTopics(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_TOPICS;
    return this.postRequest(authUser, url, payload);
  },

  // fetchOptimizationUnclassifiedMessages(authUser, payload) {
  //   let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES;
  //   return this.postRequest(authUser, url, payload);
  // },

  fetchOptimizationUnclassifiedMessagesWithToBeActioned(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES_WITH_TO_BE_ACTIONED;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationUnclassifiedMessagesWithActioned(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES_WITH_ACTIONED;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationUnclassifiedMessagesWithDiscard(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_MESSAGES_WITH_DISCARD;
    return this.postRequest(authUser, url, payload);
  },

  saveOptimizationUnclassifiedMessages(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_SAVE_MESSAGES;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationUnclassifiedIntents(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_UNCLASSIFIED_INTENTS;
    return this.postRequest(authUser, url, payload);
  },

  fetchOptimizationResponsePerformanceSummary(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_FETCH_RESPONSE_PERFORMANCE_SUMMARY;
    return this.postRequest(authUser, url, payload);
  },

  addResponse(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_ADD_RESPONSE;
    return this.postRequest(authUser, url, payload);
  },

  removeResponse(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_REMOVE_RESPONSE;
    return this.postRequest(authUser, url, payload);
  },

  addIntent(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_ADD_INTENT;
    return this.postRequest(authUser, url, payload);
  },

  removeIntent(authUser, payload) {
    let url = Vue.WyConstants.SERVER.OPTIMIZATION_HOST + this.API.OPTIMIZATION_REMOVE_INTENT;
    return this.postRequest(authUser, url, payload);
  },
};
